/**
 * Global filters business logic operations - separated from React state
 */

import { getCurrentMonthRange } from '@/utils/dateUtils';
import type { GlobalFilters } from './types';

const STORAGE_KEY = 'global-filters';

/**
 * Default filter values
 */
export const getDefaultFilters = (): GlobalFilters => ({
  customDateFilter: getCurrentMonthRange(),
  includeFutures: false,
  selectedBanks: [],
  selectedCards: [],
  selectedPaymentMethods: [],
  selectedCategories: [],
  transactionTypeFilter: 'all'
});

/**
 * Load filters from localStorage
 */
export const loadFiltersFromStorage = (): GlobalFilters => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return { ...getDefaultFilters(), ...JSON.parse(saved) };
    }
  } catch (error) {
    console.error('[GlobalFilters] Failed to load filters:', error);
  }
  return getDefaultFilters();
};

/**
 * Save filters to localStorage
 */
export const saveFiltersToStorage = (filters: GlobalFilters): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('[GlobalFilters] Failed to save filters:', error);
  }
};

/**
 * Check if any filters are active (different from defaults)
 */
export const checkHasActiveFilters = (filters: GlobalFilters): boolean => {
  const current = getCurrentMonthRange();
  return (
    filters.customDateFilter.startDate !== current.startDate ||
    filters.customDateFilter.endDate !== current.endDate ||
    filters.includeFutures ||
    filters.selectedBanks.length > 0 ||
    filters.selectedCards.length > 0 ||
    filters.selectedPaymentMethods.length > 0 ||
    filters.selectedCategories.length > 0 ||
    filters.transactionTypeFilter !== 'all'
  );
};

/**
 * Merge partial filter updates with current filters
 */
export const mergeFilters = (
  current: GlobalFilters,
  updates: Partial<GlobalFilters>
): GlobalFilters => ({
  ...current,
  ...updates
});
